import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import type { BulkUser } from './BulkActionBar.js';

export interface BulkRowResult {
  user_id: string;
  ok: boolean;
  code?: string;
  message?: string;
}

export interface BulkResponse {
  results: BulkRowResult[];
  summary: { requested: number; succeeded: number; failed: number };
}

interface BulkResultDialogProps {
  result: BulkResponse | null;
  /** Currently listed users, to label rows with emails; unknown ids fall back to the raw id. */
  users: BulkUser[];
  onClose: () => void;
}

/** 037 FR-011: per-row outcome report — every requested user exactly once,
 * failures with their server message verbatim, plus summary counts. A run
 * where every row failed is still a completed run (not an error state). */
export function BulkResultDialog({ result, users, onClose }: BulkResultDialogProps) {
  if (!result) return null;

  const emailById = new Map(users.map((u) => [u._id, u.email]));

  return (
    <Modal open onClose={onClose} title="Bulk action results">
      <div className="px-6 py-4 space-y-4 text-sm">
        <p className="text-steel">
          {result.summary.succeeded} succeeded, {result.summary.failed} failed (
          {result.summary.requested} requested)
        </p>
        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {result.results.map((row) => (
            <li
              key={row.user_id}
              data-testid="bulk-result-row"
              className="flex items-start justify-between gap-4 rounded border border-hairline-soft px-3 py-2"
            >
              <span className="text-ink">{emailById.get(row.user_id) ?? row.user_id}</span>
              {row.ok ? (
                <span className="shrink-0 font-medium text-green-600">OK</span>
              ) : (
                <span className="text-right text-red-600">{row.message ?? 'Failed'}</span>
              )}
            </li>
          ))}
        </ul>
        <div className="flex justify-end">
          <Button size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}
